import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Input } from '@pijma/crypto'
import { useBaseMask, useNumericMask } from '@pijma/input'

import { Dispatch, RootState } from '@stores'
import { TPayFormState } from '@stores/types/*'
import {
  charIsEnSymbol,
  charIsNumOrLatinLetterSymbol,
  replaceNotEnSymbol,
  replaceNotNumOrLatinLetterSymbol,
} from '@utils'
import styled from 'styled-components'

import {
  SESSION_IMPS_ACCOUNT_KEY,
  SESSION_IMPS_ACCOUNT_NUM_KEY,
  SESSION_IMPS_IFSC_KEY,
} from '../../../../stores/payform/payForm'
import { TPaymentGateway } from '../../../../stores/types/TPaymentGatewayState'
import { H4 } from '../../../common/header/Headings'
import { Island } from '../../../common/island'
import NavigationBar from '../../../common/navigation/NavigationBar'
import { BodyText500 } from '../../../common/text/BodyText'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 0;
  margin-bottom: 24px;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 20px;
  margin-top: 32px;
`

const SourceTitle = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
  width: 100%;
  padding: 20px 24px 16px;
`

const NavigationBarWrapper = styled.div`
  padding: 0 4px;
  display: flex;
  width: 100%;
  align-items: start;
`

const BackContainer = styled.div`
  margin: 24px 0 0 0;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  payForm: TPayFormState
  paymentGateway?: TPaymentGateway
  paymentBlocking?: boolean
}

interface DispatchProps {
  setImpsAccount: (impsAccount: string) => void
  setImpsAccountNum: (impsAccountNum: string) => void
  setIfsc: (ifsc: string) => void
  doPaymentBlocking: () => void
  resetPaymentBlocking: () => void
}

type Props = StateProps & DispatchProps

const SellImpsMethodPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()

  const [formValid, setFormValid] = useState<boolean>(false)

  useEffect(() => {
    setFormValid(
      props.payForm.impsAccount !== undefined &&
        props.payForm.impsAccountNum !== undefined &&
        props.payForm.ifsc !== undefined &&
        props.payForm.impsAccount !== '' &&
        props.payForm.impsAccountNum !== '' &&
        props.payForm.ifsc !== '',
    )
  }, [
    props.payForm.impsAccount,
    props.payForm.impsAccountNum,
    props.payForm.ifsc,
  ])

  // Pijma input masks
  const impsAccountMask = useBaseMask({
    type: 'text',
    format: replaceNotEnSymbol,
    isValidInputCharacter: charIsEnSymbol,
    removeFormatting: replaceNotEnSymbol,
    value: props.payForm.impsAccount,
    onValueChange: ({ formattedValue }) => {
      props.setImpsAccount(formattedValue)
    },
  })

  const impsAccountNumMask = useNumericMask({
    value: props.payForm.impsAccountNum,
    thousandsGroupStyle: 'thousand',
    thousandSeparator: '',
    staticSeparator: true,
    allowLeadingZeros: false,
    allowNegative: false,
    onValueChange: ({ formattedValue }) => {
      props.setImpsAccountNum(formattedValue)
    },
  })

  const ifscMask = useBaseMask({
    type: 'text',
    format: replaceNotNumOrLatinLetterSymbol,
    isValidInputCharacter: charIsNumOrLatinLetterSymbol,
    removeFormatting: replaceNotNumOrLatinLetterSymbol,
    value: props.payForm.ifsc,
    onValueChange: ({ formattedValue }) => {
      props.setIfsc(formattedValue)
    },
  })

  const goToSumPage = () => {
    sessionStorage.setItem(
      SESSION_IMPS_ACCOUNT_KEY,
      props.payForm.impsAccount + '',
    )
    sessionStorage.setItem(
      SESSION_IMPS_ACCOUNT_NUM_KEY,
      props.payForm.impsAccountNum + '',
    )
    sessionStorage.setItem(SESSION_IMPS_IFSC_KEY, props.payForm.ifsc + '')
    redirectTo(`../sum`)
  }

  const backLink = '../source/IMPS'

  return (
    <Container>
      <NavigationBarWrapper>
        <NavigationBar
          paymentDisabled
          sumDisabled
          dataRedirectLink={backLink}
          showMethod
          noPaymentMethod={true}
        />
      </NavigationBarWrapper>
      <Island size="M" bgColor="primary">
        <SourceTitle>
          <H4>
            {intl.formatMessage({ id: 'sellSourceImpsMethodPage.title' })}
          </H4>
        </SourceTitle>
        <InputContainer>
          <Input
            {...impsAccountMask}
            label={intl.formatMessage({
              id: 'sellSourceImpsMethodPage.accountNameLabel',
            })}
            width={'100%'}
            disabled={props.paymentBlocking}
            type={'text'}
            allowClear={true}
          />
        </InputContainer>
        <InputContainer>
          <Input
            {...impsAccountNumMask}
            label={intl.formatMessage({
              id: 'sellSourceImpsMethodPage.accountNumLabel',
            })}
            width={'100%'}
            inputMode={'decimal'}
            disabled={props.paymentBlocking}
            type={'text'}
            allowClear={true}
          />
        </InputContainer>
        <InputContainer>
          <Input
            {...ifscMask}
            label={intl.formatMessage({
              id: 'sellSourceImpsMethodPage.ifscLabel',
            })}
            width={'100%'}
            disabled={props.paymentBlocking}
            type={'text'}
            allowClear={true}
          />
        </InputContainer>
        <ButtonContainer>
          <Button
            width={'100%'}
            size={'m'}
            disabled={!formValid}
            onPress={() => goToSumPage()}
          >
            {intl.formatMessage({ id: 'sellSourceImpsMethodPage.goToSumPage' })}
          </Button>
        </ButtonContainer>
      </Island>
      <BackContainer onClick={() => redirectTo(backLink)}>
        <BodyText500>
          {intl.formatMessage({ id: 'sellSourceImpsMethodPage.back' })}
        </BodyText500>
      </BackContainer>
    </Container>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  paymentGateway: state.payForm?.paymentGateway,
  paymentBlocking: state.payForm?.paymentBlocking,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setImpsAccount: (impsAccount: string) =>
      dispatch.payForm.set({
        impsAccount,
      }),
    setImpsAccountNum: (impsAccountNum: string) =>
      dispatch.payForm.set({
        impsAccountNum,
      }),
    setIfsc: (ifsc: string) =>
      dispatch.payForm.set({
        ifsc,
      }),
    doPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: true,
      }),
    resetPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: false,
      }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SellImpsMethodPage)
